import { useContext } from 'react';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { useGetCaseDetails } from '../../api/casedetails';
import UserProfileContext from '../../context/userprofilecontext.js';
//import { getTableHeaders } from './table-asset-liability.js';
import { formatDate, formatCurrency, formatOwnershipAllocation, collectAbbreviationLegend, collectTitleAllocations } from '../../utils/formatters.js';
import { addFootersToAllPages, normalizeMargin, standardPDFStyles, addPageIfNeeded } from '../shared/PDFCommon.js';
import { buildAssignmentLayout, PERSONAL_ASSIGNMENT_KEY, ENTITY_ASSIGNMENTS_LABEL } from '../../utils/accountAssignment';



const calculateSourceValues = (account) => {
    const end_balance = account.latest_balance.end_balance || 0;
    const petitionerSource = account._petitioner_owning_pct || 0;
    const petitionerValue = account.latest_balance._petitioner_owning_value ?? end_balance * petitionerSource;
    const respondentSource = account._respondent_owning_pct || 0;
    const respondentValue = account.latest_balance._respondent_owning_value ?? end_balance * respondentSource;
    const otherSource = account._stated_other_pct || 0;
    const otherValue = end_balance * otherSource;
    const maritalSource = account._stated_marital_pct ?? (1 - petitionerSource - respondentSource - otherSource);
    const petitionerSettlement = account._petitioner_settlement_pct || 0;
    const maritalValue = end_balance * maritalSource;
    const petitionerSettlementValue = account.latest_balance._petitioner_settlement_value ?? maritalValue * petitionerSettlement;
    const respondentSettlementValue = maritalValue - petitionerSettlementValue;

    return {
        value: end_balance,
        petitionerValue: petitionerValue,
        respondentValue: respondentValue,
        otherValue,
        maritalValue,
        petitionerSource,
        respondentSource,
        otherSource,
        maritalSource,
        petitionerSettlement,
        respondentSettlement: 1 - petitionerSettlement,
        petitionerSettlementValue,
        respondentSettlementValue
    };
};

// Calculate equity source values for accounts with associated liabilities
const calculateEquitySourceValues = (account) => {
    const accountSourceValues = calculateSourceValues(account);
    let totalValue = accountSourceValues.value;
    let totalPetitionerValue = accountSourceValues.petitionerValue;
    let totalRespondentValue = accountSourceValues.respondentValue;
    let totalOtherValue = accountSourceValues.otherValue;
    let totalMaritalValue = accountSourceValues.maritalValue;
    let totalPetitionerSettlementValue = accountSourceValues.petitionerSettlementValue;
    let totalRespondentSettlementValue = accountSourceValues.respondentSettlementValue;

    if (account.associated_liabilities) {
        account.associated_liabilities.forEach(liability => {
            // Apply parent account percentages to liability
            const liabilityValue = liability.latest_balance.end_balance || 0;
            totalValue += liabilityValue;
            totalPetitionerValue += liabilityValue * accountSourceValues.petitionerSource;
            totalRespondentValue += liabilityValue * accountSourceValues.respondentSource;
            totalOtherValue += liabilityValue * accountSourceValues.otherSource;
            const incrementalMaritalValue = liabilityValue * accountSourceValues.maritalSource;
            totalMaritalValue += incrementalMaritalValue;
            const incrementalPetitionerSettlement = incrementalMaritalValue * accountSourceValues.petitionerSettlement;
            totalPetitionerSettlementValue += incrementalPetitionerSettlement;
            const incrementalRespondentSettlement = incrementalMaritalValue - incrementalPetitionerSettlement;
            totalRespondentSettlementValue += incrementalRespondentSettlement;
        });
    }

    return {
        value: totalValue,
        petitionerValue: totalPetitionerValue,
        respondentValue: totalRespondentValue,
        otherValue: totalOtherValue,
        maritalValue: totalMaritalValue,
        petitionerSettlementValue: totalPetitionerSettlementValue,
        respondentSettlementValue: totalRespondentSettlementValue
    };
};

// const calculateAccountNetValue = (account) => {
//     let netValue = account.latest_balance.end_balance;
//     if (account.associated_liabilities) {
//       netValue += account.associated_liabilities.reduce((sum, liab) => sum + liab.latest_balance.end_balance, 0);
//     }
//     return netValue;
//   };


// const calculateSectionTotal = (section) => {
//     const accounts = section.accounts || [];
//     return accounts.reduce((sum, account) => {
//       return sum + calculateAccountNetValue(account);
//     }, 0);
//   };

const calculateCategoryTotals = (accounts) => {
    //const accounts = section.accounts || [];
    let valueTotal = 0;
    let petitionerTotal = 0;
    let respondentTotal = 0;
    let otherTotal = 0;
    let maritalTotal = 0;
    let petitionerSettlementTotal = 0;
    let respondentSettlementTotal = 0;

    accounts.forEach(account => {
        const sourceValues = calculateSourceValues(account);
        valueTotal += sourceValues.value;
        petitionerTotal += sourceValues.petitionerValue;
        respondentTotal += sourceValues.respondentValue;
        otherTotal += sourceValues.otherValue;
        maritalTotal += sourceValues.maritalValue;
        petitionerSettlementTotal += sourceValues.petitionerSettlementValue;
        respondentSettlementTotal += sourceValues.respondentSettlementValue;

        // Add associated liabilities
        if (account.associated_liabilities) {
            account.associated_liabilities.forEach(liability => {
                // Use parent account percentages for liabilities
                const liabilityValue = liability.latest_balance.end_balance || 0;
                valueTotal += liabilityValue;
                petitionerTotal += liabilityValue * sourceValues.petitionerSource;
                respondentTotal += liabilityValue * sourceValues.respondentSource;
                otherTotal += liabilityValue * sourceValues.otherSource;
                const incrementalMaritalValue = liabilityValue * sourceValues.maritalSource;
                maritalTotal += incrementalMaritalValue;
                const incrementalPetitionerSettlement = incrementalMaritalValue * sourceValues.petitionerSettlement;
                petitionerSettlementTotal += incrementalPetitionerSettlement;
                const incrementalRespondentSettlement = incrementalMaritalValue - incrementalPetitionerSettlement;
                respondentSettlementTotal += incrementalRespondentSettlement;
            });
        }
    });

    return { valueTotal, petitionerTotal, respondentTotal, otherTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal };
};

//   const calculateCategoryTotal = (data, category) => {
//     return Object.entries(data[category]).reduce((sum, [_, section]) => {
//       return sum + calculateSectionTotal(section);
//     }, 0);
//   };

const calculateAssetLiabilitySectionTotals = (sectionData) => {

    let valueTotal = 0;
    let petitionerTotal = 0;
    let respondentTotal = 0;
    let otherTotal = 0;
    let maritalTotal = 0;
    let petitionerSettlementTotal = 0;
    let respondentSettlementTotal = 0;

    for (const [categoryName, categoryData] of Object.entries(sectionData)) {
        const categoryTotals = calculateCategoryTotals(categoryData.accounts);

        valueTotal += categoryTotals.valueTotal;
        petitionerTotal += categoryTotals.petitionerTotal;
        respondentTotal += categoryTotals.respondentTotal;
        otherTotal += categoryTotals.otherTotal;
        maritalTotal += categoryTotals.maritalTotal;
        petitionerSettlementTotal += categoryTotals.petitionerSettlementTotal;
        respondentSettlementTotal += categoryTotals.respondentSettlementTotal;
    };

    return { valueTotal, petitionerTotal, respondentTotal, otherTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal };
};

// Amount columns must never wrap: "8,297,405.94" split across two lines is unreadable
// on a court-facing document. Amount columns are sized 'auto' by jspdf-autotable and
// share whatever the fixed-width text columns leave over, which is too little for
// 7+ digit amounts when the source and settlement columns are all shown.
const AMOUNT_DATA_KEYS = ["value", "petitionerValue", "respondentValue", "otherValue", "maritalValue", "petitionerSettlementValue", "respondentSettlementValue"];
// Free-text columns wrap cleanly between words, so they give up width first; Title and
// Source hold short single words ("Statement") that would split mid-word if squeezed.
const SHRINKABLE_TEXT_KEY_TIERS = [["account", "notes"], ["title", "source"]];
const TABLE_FONT = "times";
const TABLE_FONT_SIZE = 5;
const DEFAULT_CELL_PADDING = 5;      // jspdf-autotable's default, in pt
const TIGHT_AMOUNT_CELL_PADDING = 2; // horizontal padding used only when amounts would otherwise wrap
const MIN_TEXT_COLUMN_RATIO = 0.5;   // a text column never gives up more than half its configured width

// Width of the widest amount string in the rows, measured in every font style the
// table uses (totals are bold, associated liabilities italic).
export const measureAmountTextWidth = (doc, rows) => {
    const previousFont = doc.getFont();
    const previousFontSize = doc.getFontSize();
    doc.setFontSize(TABLE_FONT_SIZE);
    let widest = 0;
    ["normal", "bold", "italic", "bolditalic"].forEach((fontStyle) => {
        doc.setFont(TABLE_FONT, fontStyle);
        rows.forEach((row) => {
            AMOUNT_DATA_KEYS.forEach((key) => {
                if (row[key]) {
                    widest = Math.max(widest, doc.getTextWidth(String(row[key])));
                }
            });
        });
    });
    doc.setFont(previousFont.fontName, previousFont.fontStyle);
    doc.setFontSize(previousFontSize);
    return widest;
};

// Builds every asset/liability row the report will print (without drawing anything)
// and measures the widest amount, so every table in the report gets the same column
// layout. includeReportTotals adds the MBS-only Net Worth and Final Settlement rows.
export const measureReportAmountTextWidth = (doc, assetLiabilityData, displayOptions, { includeReportTotals = false, groupByAssignment = false } = {}) => {
    const rows = [];
    let rowIndex = 1;
    ["assets", "liabilities"].forEach((whichSection) => {
        const sectionData = assetLiabilityData?.[whichSection] || {};
        const layout = buildAssignmentLayout(sectionData, { enabled: groupByAssignment });
        layout.forEach(({ assignment, sections }) => {
            Object.entries(sections).forEach(([categoryName, categoryData]) => {
                let categoryRows;
                [categoryRows, rowIndex] = formatAccountTableData(rowIndex, categoryName, categoryData.accounts || [], displayOptions);
                rows.push(...categoryRows);
            });
            if (assignment) {
                const [groupTotalRows] = formatAssetLiabilitySectionTotalRow(
                    rowIndex,
                    `${assignment.label} ${whichSection === "assets" ? "Assets" : "Liabilities"}`,
                    sections,
                    displayOptions
                );
                rows.push(...groupTotalRows);
            }
        });
        const [sectionTotalRows] = formatAssetLiabilitySectionTotalRow(rowIndex, whichSection === "assets" ? "Assets" : "Liabilities", sectionData, displayOptions);
        rows.push(...sectionTotalRows);
    });
    if (includeReportTotals) {
        const [netWorthRows] = formatAssetLiabilityNetWorthTotalRow(rowIndex, assetLiabilityData, displayOptions);
        rows.push(...netWorthRows);
        if (displayOptions.showSettlementColumns) {
            const [finalAdjustmentRows] = formatFinalAdjustmentTable(rowIndex, assetLiabilityData, displayOptions);
            rows.push(...finalAdjustmentRows);
        }
    }
    return measureAmountTextWidth(doc, rows);
};

// Fits the amount columns to the widest amount. When the space the text columns leave
// over is already enough, the configured widths and padding are kept unchanged. Otherwise
// the amount cells get tighter horizontal padding and, if that is still not enough, the
// text columns give up width in proportion to their size.
export const fitAmountColumns = (textWidths, amountColumnCount, amountTextWidth, tableWidth) => {
    const widths = { ...textWidths };
    const sumWidths = () => Object.values(widths).reduce((sum, width) => sum + width, 0);
    const available = tableWidth - sumWidths();

    // Enough room: keep the text columns and padding as configured. The minimum width
    // still stops autotable from squeezing an amount column below the widest amount
    // when long header words compete for the same space.
    const defaultAmountMinWidth = amountTextWidth + 2 * DEFAULT_CELL_PADDING;
    if (amountColumnCount * defaultAmountMinWidth <= available) {
        return { widths, amountCellPadding: null, amountMinWidth: defaultAmountMinWidth };
    }

    const amountMinWidth = amountTextWidth + 2 * TIGHT_AMOUNT_CELL_PADDING;
    let deficit = amountColumnCount * amountMinWidth - available;
    SHRINKABLE_TEXT_KEY_TIERS.forEach((tier) => {
        if (deficit <= 0) {
            return;
        }
        const shrinkableKeys = tier.filter((key) => widths[key] !== undefined);
        const shrinkableTotal = shrinkableKeys.reduce((sum, key) => sum + widths[key] * (1 - MIN_TEXT_COLUMN_RATIO), 0);
        if (shrinkableTotal <= 0) {
            return;
        }
        const ratio = Math.min(1, deficit / shrinkableTotal);
        shrinkableKeys.forEach((key) => {
            widths[key] -= widths[key] * (1 - MIN_TEXT_COLUMN_RATIO) * ratio;
        });
        deficit -= shrinkableTotal * ratio;
    });
    return {
        widths,
        amountCellPadding: { top: DEFAULT_CELL_PADDING, bottom: DEFAULT_CELL_PADDING, left: TIGHT_AMOUNT_CELL_PADDING, right: TIGHT_AMOUNT_CELL_PADDING },
        amountMinWidth,
    };
};

export const getAccountTableHeaders = (displayOptions, amountTextWidth = 0, tableWidth = 468) => {

    let configuration = "base";
    if (displayOptions.showSourceColumns) {
        configuration += "_source";
    }
    if (displayOptions.showSettlementColumns) {
        configuration += "_settlement";
    }
    if (displayOptions.showNotes) {
        configuration += "_notes";
    }
    const cellWidths = {
        base: {
            index: 18,
            account: 217,
            title: 72,
            source: 72,
            date: 35,
            notes: 65
        },
        base_source: {
            index: 18,
            account: 144,
            title: 65,
            source: 51,
            date: 35,
            notes: 65
        },
        base_source_settlement: {
            index: 18,
            account: 80,
            title: 65,
            source: 51,
            date: 35,
            notes: 65
        },
        base_source_settlement_notes: {  // reviewed
            index: 18,
            account: 75,
            title: 44,
            source: 33,
            date: 35,
            notes: 60
        },
        base_source_notes: {
            index: 18,
            account: 108,
            title: 51,
            source: 36,
            date: 35,
            notes: 65
        },
        base_settlement: {
            index: 18,
            account: 144,
            title: 65,
            source: 69,
            date: 35,
            notes: 65
        },
        base_settlement_notes: {
            index: 18,
            account: 108,
            title: 50,
            source: 33,
            date: 35,
            notes: 87
        },
        base_notes: {
            index: 18,
            account: 144,
            title: 72,
            source: 40,
            date: 35,
            notes: 80
        }
    }
    const { notes: notesWidth, ...shownTextWidths } = cellWidths[configuration];
    const textWidths = displayOptions.showNotes ? { ...shownTextWidths, notes: notesWidth } : shownTextWidths;
    const amountColumnCount = 1 + (displayOptions.showSourceColumns ? 4 : 0) + (displayOptions.showSettlementColumns ? 2 : 0);
    const { widths, amountCellPadding, amountMinWidth } = fitAmountColumns(textWidths, amountColumnCount, amountTextWidth, tableWidth);

    const defaultColumnStyle = {}
    const defaultCurrencyStyle = { halign: "right", minCellWidth: amountMinWidth, ...(amountCellPadding && { cellPadding: amountCellPadding }) };
    let headers = [
        { header: "#", dataKey: "index", columnStyle: { ...defaultColumnStyle, cellWidth: widths.index, halign: "center" } },
        { header: "Account", dataKey: "account", columnStyle: { ...defaultColumnStyle, cellWidth: widths.account } },
        { header: "Title", dataKey: "title", columnStyle: { ...defaultColumnStyle, cellWidth: widths.title } },
        { header: "Source", dataKey: "source", columnStyle: { ...defaultColumnStyle, cellWidth: widths.source } },
        { header: "Date", dataKey: "date", columnStyle: { ...defaultColumnStyle, cellWidth: widths.date, halign: "center" } },
        { header: "Value", dataKey: "value", columnStyle: { ...defaultColumnStyle, ...defaultCurrencyStyle } }
    ];
    if (displayOptions.showSourceColumns) {
        headers.push(
            { header: "Petitioner Source", dataKey: "petitionerValue", columnStyle: { ...defaultColumnStyle, ...defaultCurrencyStyle } },
            { header: "Respondent Source", dataKey: "respondentValue", columnStyle: { ...defaultColumnStyle, ...defaultCurrencyStyle } },
            { header: "Other Source", dataKey: "otherValue", columnStyle: { ...defaultColumnStyle, ...defaultCurrencyStyle } },
            { header: "Marital Source", dataKey: "maritalValue", columnStyle: { ...defaultColumnStyle, ...defaultCurrencyStyle } }
        );
    }
    if (displayOptions.showSettlementColumns) {
        headers.push(
            { header: "Petitioner Settlement", dataKey: "petitionerSettlementValue", columnStyle: { ...defaultColumnStyle, ...defaultCurrencyStyle } },
            { header: "Respondent Settlement", dataKey: "respondentSettlementValue", columnStyle: { ...defaultColumnStyle, ...defaultCurrencyStyle } }
        );
    }
    if (displayOptions.showNotes) {
        headers.push({ header: "Notes", dataKey: "notes", columnStyle: { ...defaultColumnStyle, cellWidth: widths.notes } });
    }
    return headers;
};

const calculateAccountTableColumnStyles = (headers) => {
    let columnStyles = {};
    headers.forEach((header, index) => {
        columnStyles[index] = header.columnStyle || {};
    });
    return columnStyles;
};

const formatAssetLiabilitySectionTotalRow = (index, sectionName, sectionData, displayOptions) => {


    const sectionTotals = calculateAssetLiabilitySectionTotals(sectionData);
    const sectionTotalRow = {
        rowType: "asset_liability_total",
        index: index,
        account: `Total ${sectionName}`,
        title: "",
        source: "",
        date: "",
        value: formatCurrency(sectionTotals.valueTotal),
    };
    if (displayOptions.showSourceColumns) {
        sectionTotalRow.petitionerValue = formatCurrency(sectionTotals.petitionerTotal);
        sectionTotalRow.respondentValue = formatCurrency(sectionTotals.respondentTotal);
        sectionTotalRow.otherValue = formatCurrency(sectionTotals.otherTotal);
        sectionTotalRow.maritalValue = formatCurrency(sectionTotals.maritalTotal);
    }
    if (displayOptions.showSettlementColumns) {
        sectionTotalRow.petitionerSettlementValue = formatCurrency(sectionTotals.petitionerSettlementTotal);
        sectionTotalRow.respondentSettlementValue = formatCurrency(sectionTotals.respondentSettlementTotal);
    }
    index += 1;
    const result = [sectionTotalRow]
    return [result, index];
}


const formatAssetLiabilityNetWorthTotalRow = (index, data, displayOptions) => {
    const assetTotals = calculateAssetLiabilitySectionTotals(data["assets"]);
    const liabilityTotals = calculateAssetLiabilitySectionTotals(data["liabilities"]);
    const sectionTotalRow = {
        rowType: "asset_liability_total",
        index: index,
        account: `Total Net Worth`,
        title: "",
        source: "",
        date: "",
        value: formatCurrency(assetTotals.valueTotal + liabilityTotals.valueTotal),
        // value: formatCurrency(999999999.99),
    };
    if (displayOptions.showSourceColumns) {
        sectionTotalRow.petitionerValue = formatCurrency(assetTotals.petitionerTotal + liabilityTotals.petitionerTotal);
        sectionTotalRow.respondentValue = formatCurrency(assetTotals.respondentTotal + liabilityTotals.respondentTotal);
        sectionTotalRow.otherValue = formatCurrency(assetTotals.otherTotal + liabilityTotals.otherTotal);
        sectionTotalRow.maritalValue = formatCurrency(assetTotals.maritalTotal + liabilityTotals.maritalTotal);
        // sectionTotalRow.petitionerValue = formatCurrency(999999999.99);
        // sectionTotalRow.respondentValue = formatCurrency(999999999.99);
        // sectionTotalRow.maritalValue = formatCurrency(999999999.99);
    }
    if (displayOptions.showSettlementColumns) {
        sectionTotalRow.petitionerSettlementValue = formatCurrency(assetTotals.petitionerSettlementTotal + liabilityTotals.petitionerSettlementTotal);
        sectionTotalRow.respondentSettlementValue = formatCurrency(assetTotals.respondentSettlementTotal + liabilityTotals.respondentSettlementTotal);
        // sectionTotalRow.petitionerSettlementValue = formatCurrency(999999999.99);
        // sectionTotalRow.respondentSettlementValue = formatCurrency(999999999.99);
    }
    index += 1;
    const result = [sectionTotalRow]
    return [result, index];
}

const formatFinalAdjustmentTable = (index, data, displayOptions) => {
    const assetTotals = calculateAssetLiabilitySectionTotals(data["assets"]);
    const liabilityTotals = calculateAssetLiabilitySectionTotals(data["liabilities"]);

    const settlementAdjustment = data?.settlement_adjustment || {};
    const petitionerFinalAdjustment = settlementAdjustment?.petitioner_final_adjustment || 0;
    const respondentFinalAdjustment = -(settlementAdjustment?.petitioner_final_adjustment || 0);
    const finalAdjustmentNotes = settlementAdjustment?.notes || '';

    const adjustmentRow = {
        rowType: "body",
        index: index,
        account: `Final Settlement Adjustment`,
        title: "",
        source: "",
        date: "",
        value: "",
        // value: formatCurrency(999999999.99),
    };
    index += 1;
    const sectionTotalRow = {
        rowType: "asset_liability_total",
        index: index,
        account: `Final Settlement Amount`,
        title: "",
        source: "",
        date: "",
        value: formatCurrency(assetTotals.valueTotal + liabilityTotals.valueTotal),
        // value: formatCurrency(999999999.99),
    };
    if (displayOptions.showSourceColumns) {
        sectionTotalRow.petitionerValue = formatCurrency(assetTotals.petitionerTotal + liabilityTotals.petitionerTotal);
        sectionTotalRow.respondentValue = formatCurrency(assetTotals.respondentTotal + liabilityTotals.respondentTotal);
        sectionTotalRow.otherValue = formatCurrency(assetTotals.otherTotal + liabilityTotals.otherTotal);
        sectionTotalRow.maritalValue = formatCurrency(assetTotals.maritalTotal + liabilityTotals.maritalTotal);
        // sectionTotalRow.petitionerValue = formatCurrency(999999999.99);
        // sectionTotalRow.respondentValue = formatCurrency(999999999.99);
        // sectionTotalRow.maritalValue = formatCurrency(999999999.99);
    }
    if (displayOptions.showSettlementColumns) {
        // const finalPetitionerTotal = petitionerSettlementTotal + petitionerFinalAdjustment;
        // const finalRespondentTotal = respondentSettlementTotal + respondentFinalAdjustment;
        sectionTotalRow.petitionerSettlementValue = formatCurrency(assetTotals.petitionerSettlementTotal + liabilityTotals.petitionerSettlementTotal + petitionerFinalAdjustment);
        sectionTotalRow.respondentSettlementValue = formatCurrency(assetTotals.respondentSettlementTotal + liabilityTotals.respondentSettlementTotal + respondentFinalAdjustment);
        adjustmentRow.petitionerSettlementValue = formatCurrency(petitionerFinalAdjustment);
        adjustmentRow.respondentSettlementValue = formatCurrency(respondentFinalAdjustment);
        // sectionTotalRow.petitionerSettlementValue = formatCurrency(999999999.99);
        // sectionTotalRow.respondentSettlementValue = formatCurrency(999999999.99);
    }
    if (displayOptions.showNotes) {
        adjustmentRow.notes = finalAdjustmentNotes || "";
    }
    index += 1;
    const result = [adjustmentRow, sectionTotalRow]
    return [result, index];
}

const formatAccountTableData = (startIndex, categoryName, accountTableData, displayOptions, isAssociatedLiability = false) => {

    const formatNetEquityRow = (index, account) => {
        const netEquitySourceValues = calculateEquitySourceValues(account)
        const netEquityRow = {
            rowType: "net_equity",
            index: index,
            account: `${account._displayName} Net Equity`,
            title: "",
            source: "",
            date: "",
            value: formatCurrency(netEquitySourceValues.value),
        };
        if (displayOptions.showSourceColumns) {
            netEquityRow.petitionerValue = formatCurrency(netEquitySourceValues.petitionerValue);
            netEquityRow.respondentValue = formatCurrency(netEquitySourceValues.respondentValue);
            netEquityRow.otherValue = formatCurrency(netEquitySourceValues.otherValue);
            netEquityRow.maritalValue = formatCurrency(netEquitySourceValues.maritalValue);
        }
        if (displayOptions.showSettlementColumns) {
            netEquityRow.petitionerSettlementValue = formatCurrency(netEquitySourceValues.petitionerSettlementValue);
            netEquityRow.respondentSettlementValue = formatCurrency(netEquitySourceValues.respondentSettlementValue);
        }
        index += 1;
        return [netEquityRow, index];
    }

    const formatAccountRow = (index, account, isAssociatedLiability = false) => {
        const sourceValues = calculateSourceValues(account);
        let result = [];
        const accountRow = {
            rowType: isAssociatedLiability ? "associated_liability" : "account",
            index: index,
            account: account._displayName || "",
            title: formatOwnershipAllocation(account.title, { abbreviate: true }) || account.account_holder || "",
            source: account.latest_balance?._source || "",
            date: formatDate(account.latest_balance?.end_date) || "",
            value: formatCurrency(account.latest_balance?.end_balance),
        };
        if (displayOptions.showSourceColumns) {
            accountRow.petitionerValue = formatCurrency(sourceValues.petitionerValue);
            accountRow.respondentValue = formatCurrency(sourceValues.respondentValue);
            accountRow.otherValue = formatCurrency(sourceValues.otherValue);
            accountRow.maritalValue = formatCurrency(sourceValues.maritalValue);
        }
        if (displayOptions.showSettlementColumns) {
            accountRow.petitionerSettlementValue = formatCurrency(sourceValues.petitionerSettlementValue);
            accountRow.respondentSettlementValue = formatCurrency(sourceValues.respondentSettlementValue);
        }
        if (displayOptions.showNotes) {
            accountRow.notes = account.notes || "";
        }
        index += 1;

        if (isAssociatedLiability) {
            result.push(accountRow);
            return [result, index];
        }

        if (!account.associated_liabilities || account.associated_liabilities.length === 0) {
            result.push(accountRow);
            return [result, index];
        }



        let [associatedLiabilityRows, newAccountIndex] = formatAccountTableData(index, categoryName, account.associated_liabilities, displayOptions, true);
        index = newAccountIndex;
        result.push(accountRow);
        result.push(...associatedLiabilityRows);
        let [netEquityRow, lastIndex] = formatNetEquityRow(index, account);
        result.push(netEquityRow);

        return [result, lastIndex];
    }

    const formatCategoryTotalRow = (index) => {
        const categoryTotals = calculateCategoryTotals(accountTableData);
        const categoryTotalRow = {
            rowType: "category_total",
            index: index,
            account: `${categoryName} Total`,
            title: "",
            source: "",
            date: "",
            value: formatCurrency(categoryTotals.valueTotal),
        };
        if (displayOptions.showSourceColumns) {
            categoryTotalRow.petitionerValue = formatCurrency(categoryTotals.petitionerTotal);
            categoryTotalRow.respondentValue = formatCurrency(categoryTotals.respondentTotal);
            categoryTotalRow.otherValue = formatCurrency(categoryTotals.otherTotal);
            categoryTotalRow.maritalValue = formatCurrency(categoryTotals.maritalTotal);
        }
        if (displayOptions.showSettlementColumns) {
            categoryTotalRow.petitionerSettlementValue = formatCurrency(categoryTotals.petitionerSettlementTotal);
            categoryTotalRow.respondentSettlementValue = formatCurrency(categoryTotals.respondentSettlementTotal);
        }
        index += 1;
        return [categoryTotalRow, index];
    }




    let result = []
    let index = startIndex
    accountTableData.forEach((account) => {
        let [rows, newIndex] = formatAccountRow(index, account, isAssociatedLiability);

        index = newIndex;
        rows.forEach(row => {
            result.push(row);
        });
    });

    if (!isAssociatedLiability) {
        let [categoryTotalRow, newIndex] = formatCategoryTotalRow(index);

        result.push(categoryTotalRow);
        index = newIndex;
    }

    return [result, index];
};


// `headingFontSize` lets the grouped MBS step a category heading down a level
// under its Personal / business heading; every other caller keeps 14.
const addMBSAccountTable = (doc, margin, yPosition, categoryName, formattedAccountData, displayOptions, { headingFontSize = 14, amountTextWidth } = {}) => {
    // Table setup

    const normalizedMargin = normalizeMargin(margin);
    const tableWidth = doc.internal.pageSize.getWidth() - normalizedMargin.left - normalizedMargin.right;
    const widestAmount = amountTextWidth ?? measureAmountTextWidth(doc, formattedAccountData);
    const accountTableHeaders = getAccountTableHeaders(displayOptions, widestAmount, tableWidth);
    const accountTableColumnStyles = calculateAccountTableColumnStyles(accountTableHeaders);

    const cellFontSize = TABLE_FONT_SIZE;
    const textHeaderHeight = headingFontSize;
    const textHeaderSpacing = 8;
    // const totalFontSize = 10;

    const formatRow = (data) => {
        const rowType = data.row.raw.rowType   /// get the original data value of rowType

        if (rowType === "associated_liability") {
            if (data.row.section === 'body' && data.column.dataKey !== 'index') {
                if (data.column.dataKey === 'account') {
                    data.cell.text = `  ${data.cell.text}`
                }
                data.cell.styles.fontStyle = standardPDFStyles.table.body.associated_liability.fontStyle;
                data.cell.styles.textColor = standardPDFStyles.table.body.associated_liability.textColor;
            }
        }
        else if (rowType === "net_equity") {
            if (data.row.section === 'body') {
                if (data.column.dataKey === 'account') {
                    data.cell.text = `  ${data.cell.text}`
                }
                data.cell.styles.fontStyle = standardPDFStyles.table.body.net_equity.fontStyle;
                data.cell.styles.fillColor = standardPDFStyles.table.body.net_equity.fillColor;
            }
        }
        else if (rowType === "category_total") {
            if (data.row.section === 'body') {
                data.cell.styles.fontStyle = standardPDFStyles.table.body.category_total.fontStyle;
                // data.cell.styles.fontSize = totalFontSize;
                data.cell.styles.fillColor = standardPDFStyles.table.body.category_total.fillColor;
            }
        }
        else if (rowType === "asset_liability_total") {
            if (data.row.section === 'body') {
                data.cell.styles.fontStyle = standardPDFStyles.table.body.asset_liability_total.fontStyle;
                data.cell.styles.fillColor = standardPDFStyles.table.body.asset_liability_total.fillColor;
            }
        }

        if (data.row.section === 'body' && data.column.dataKey === 'index') {
            data.cell.styles.fontStyle = standardPDFStyles.table.body.index_column.fontStyle;
        }

        // columnStyles reach body cells only; give amount headers the same tightened
        // padding so "Respondent" fits the narrowed column instead of splitting mid-word.
        const amountCellPadding = accountTableColumnStyles[data.column.index]?.cellPadding;
        if (data.row.section === 'head' && amountCellPadding) {
            data.cell.styles.cellPadding = amountCellPadding;
        }

    };

    const addCategoryName = (yPosition) => {
        doc.setFontSize(textHeaderHeight);
        doc.setFont(undefined, 'bold');

        margin = normalizeMargin(margin);

        doc.text(categoryName, margin.left, yPosition, { align: 'left' });
        yPosition += textHeaderSpacing;

        return yPosition;
    }

    function shouldStartNewPage(doc, currentY, margin, options = {}) {
        const {
            textHeaderHeight = 15,   // Height for the text header above table
            textHeaderSpacing = 10,        // Space between header and table
            tableRowCount = 10,              // Number of rows in the table
            rowHeight = 15,           // Height per table row
            tableHeaderHeight = 20,        // Height of table header
            footerMargin = 30,        // Space to reserve for footer
            minRowsOnPage = 3         // Minimum rows to keep together
        } = options;

        margin = normalizeMargin(margin);

        const pageHeight = doc.internal.pageSize.height;
        const availableSpace = pageHeight - currentY - footerMargin - margin.bottom;

        // Calculate estimated table height
        const estimatedTableHeight = textHeaderSpacing + tableHeaderHeight + (tableRowCount * rowHeight);

        // If table is too big for available space, start new page
        if (estimatedTableHeight > availableSpace) {
            return true;
        }

        // If we can't fit minimum rows, start new page
        const minTableHeight = textHeaderHeight + textHeaderSpacing + tableHeaderHeight + (minRowsOnPage * rowHeight);
        if (minTableHeight > availableSpace) {
            return true;
        }

        return false;
    }

    const shouldStartNewPageOptions = {
        textHeaderHeight: textHeaderHeight,   // Height for the text header above table
        textHeaderSpacing: textHeaderSpacing,        // Space between header and table
        tableRowCount: formattedAccountData.length,              // Number of rows in the table
        rowHeight: 15,           // Height per table row
        tableHeaderHeight: 20,        // Height of table header
        footerMargin: 30,        // Space to reserve for footer
        minRowsOnPage: 3         // Minimum rows to keep together
    }

    if (shouldStartNewPage(doc, yPosition, margin, shouldStartNewPageOptions)) {
        doc.addPage();
        yPosition = normalizeMargin(margin).top;
    }

    yPosition = addCategoryName(yPosition);


    autoTable(doc, {
        columns: accountTableHeaders,
        body: formattedAccountData,
        startY: yPosition,
        pageBreak: 'avoid',
        styles: {
            overflow: 'linebreak',
            cellWidth: 'auto',
            font: TABLE_FONT,
            fontSize: cellFontSize,
            valign: 'middle',
            // halign: 'center',
        },
        headStyles: {
            fillColor: standardPDFStyles.table.header.default.fillColor,
            textColor: standardPDFStyles.table.header.default.textColor,
            halign: 'center',
        },
        columnStyles: accountTableColumnStyles,
        margin: { top: normalizeMargin(margin).top, left: normalizeMargin(margin).left, right: normalizeMargin(margin).right, bottom: normalizeMargin(margin).bottom },
        didParseCell: function (data) {
            formatRow(data);
        }
    });

    yPosition = doc.lastAutoTable.finalY + 8;

    return yPosition;

};


// Render an "Abbreviations" key mapping each user-defined short form used in the
// document's Title column back to its full legal name. Court-facing PDFs have no
// hover tooltip, so this keeps an abbreviated Title (e.g. "J (100.0%)") traceable.
// No-ops (returns yPosition unchanged) when no abbreviation is in use.
export const addAbbreviationLegend = (doc, margin, yPosition, assetLiabilityData) => {
    const legend = collectAbbreviationLegend(collectTitleAllocations(assetLiabilityData));
    if (!legend.length) return yPosition;

    yPosition = addPageIfNeeded(doc, margin, yPosition);

    doc.setFontSize(12);
    doc.setFont('times', 'bold');
    doc.text('Abbreviations', normalizeMargin(margin).left, yPosition, { align: 'left' });
    yPosition += 6;

    autoTable(doc, {
        columns: [
            { header: 'Abbreviation', dataKey: 'abbreviation' },
            { header: 'Full name', dataKey: 'display_name' },
        ],
        body: legend,
        startY: yPosition,
        pageBreak: 'auto',
        styles: { font: 'times', fontSize: 9, valign: 'middle', overflow: 'linebreak' },
        headStyles: {
            fillColor: standardPDFStyles.table.header.default.fillColor,
            textColor: standardPDFStyles.table.header.default.textColor,
            halign: 'left',
        },
        margin: { top: normalizeMargin(margin).top, left: normalizeMargin(margin).left, right: normalizeMargin(margin).right, bottom: normalizeMargin(margin).bottom },
    });

    return doc.lastAutoTable.finalY + 8;
};

// `groupByAssignment` (MBS only; the DRFA keeps its court-form layout) splits
// each side into Personal and per-entity groups with a subtotal per group,
// whenever any account is titled to a business / trust / estate. Mirrors the
// on-screen AssetsLiabilitiesTable so line numbers agree.
export const addAssetLiabilityTables = (doc, margin, rowIndex, yPosition, assetLiabilityData, whichSection, displayOptions, { groupByAssignment = false, amountTextWidth } = {}) => {

    const addSectionTitle = (yPosition, sectionTitle) => {
        const textHeaderHeight = 16;
        const textHeaderSpacing = 10;

        doc.setFontSize(textHeaderHeight);
        doc.setFont(undefined, 'bold');

        doc.text(sectionTitle, normalizeMargin(margin).left, yPosition, { align: 'left' });
        yPosition += textHeaderSpacing;

        return yPosition;
    }

    const tableYMargin = 20;

    if (whichSection !== "assets" && whichSection !== "liabilities") {
        console.error(`Invalid section: ${whichSection}`);
        return yPosition;
    }

    const sectionTitle = whichSection === "assets" ? "Assets" : "Liabilities";
    yPosition = addSectionTitle(yPosition, sectionTitle);

    // Heading levels when grouped, largest first: side title (Assets, 16) ->
    // "Business & Other Entities" (16, ruled) -> Personal / each business
    // (14, navy) -> category (12) -> the table. Mirrors the on-screen MBS.
    const ENTITIES_HEADING_SIZE = 16;
    const GROUP_HEADING_SIZE = 14;
    const GROUPED_CATEGORY_HEADING_SIZE = 12;
    const NAVY_700 = [51, 78, 104];   // --color-navy-700
    const NAVY_200 = [188, 204, 220]; // --color-navy-200

    const addEntitiesHeading = (yPosition) => {
        yPosition += tableYMargin * 1.5;
        yPosition = addPageIfNeeded(doc, margin, yPosition);
        const { left, right } = normalizeMargin(margin);
        doc.setFontSize(ENTITIES_HEADING_SIZE);
        doc.setFont(undefined, 'bold');
        doc.text(ENTITY_ASSIGNMENTS_LABEL, left, yPosition, { align: 'left' });
        doc.setDrawColor(...NAVY_200);
        doc.setLineWidth(1);
        doc.line(left, yPosition + 5, doc.internal.pageSize.getWidth() - right, yPosition + 5);
        return yPosition + 6;
    };

    const addGroupHeading = (yPosition, text) => {
        yPosition += tableYMargin;
        yPosition = addPageIfNeeded(doc, margin, yPosition);
        doc.setFontSize(GROUP_HEADING_SIZE);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...NAVY_700);
        doc.text(text, normalizeMargin(margin).left, yPosition, { align: 'left' });
        doc.setTextColor(0, 0, 0);
        return yPosition;
    };

    const sectionData = assetLiabilityData[whichSection] || {};
    const layout = buildAssignmentLayout(sectionData, { enabled: groupByAssignment });
    let entitiesHeadingShown = false;

    for (const { assignment, sections } of layout) {
        if (assignment) {
            const isPersonal = assignment.key === PERSONAL_ASSIGNMENT_KEY;
            if (!isPersonal && !entitiesHeadingShown) {
                yPosition = addEntitiesHeading(yPosition);
                entitiesHeadingShown = true;
            }
            yPosition = addGroupHeading(yPosition, assignment.kind ? `${assignment.label} (${assignment.kind})` : assignment.label);
        }
        const categoryHeading = assignment ? { headingFontSize: GROUPED_CATEGORY_HEADING_SIZE } : {};

        for (const [categoryName, categoryData] of Object.entries(sections)) {
            yPosition += tableYMargin;
            const accountTableData = categoryData.accounts || [];
            let formattedAccountData;
            [formattedAccountData, rowIndex] = formatAccountTableData(rowIndex, categoryName, accountTableData, displayOptions);
            yPosition = addMBSAccountTable(doc, margin, yPosition, categoryName, formattedAccountData, displayOptions, { ...categoryHeading, amountTextWidth });
        }

        if (assignment) {
            yPosition += tableYMargin;
            const groupName = `${assignment.label} ${sectionTitle}`;
            let groupTotalData;
            [groupTotalData, rowIndex] = formatAssetLiabilitySectionTotalRow(rowIndex, groupName, sections, displayOptions);
            yPosition = addMBSAccountTable(doc, margin, yPosition, `Total ${groupName}`, groupTotalData, displayOptions, { headingFontSize: GROUP_HEADING_SIZE, amountTextWidth });
        }
    }

    yPosition += tableYMargin;

    let sectionTotalData;

    const sectionTotalName = `Total ${sectionTitle}`;
    [sectionTotalData, rowIndex] = formatAssetLiabilitySectionTotalRow(rowIndex, sectionTitle, sectionData, displayOptions);

    yPosition = addMBSAccountTable(doc, margin, yPosition, sectionTotalName, sectionTotalData, displayOptions, { amountTextWidth });

    return [rowIndex, yPosition];
};

const usePrintMBSPDF = (data, displayOptions) => {

    const { data: caseDetails } = useGetCaseDetails();
    const userProfileContext = useContext(UserProfileContext);
    const currentCase = userProfileContext?.userProfile?.currentCase || {};
    const currentOrg = userProfileContext?.userProfile?.currentOrg || {};

    const defaultPageOrientation = 'portrait';
    const defaultUnit = 'pt';
    const defaultPageFormat = 'letter';

    const addMBSTitle = (doc, pageWidth, yPosition) => {
        // Title
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        const title = currentCase.caseName || "Petitioner v. Respondent";
        doc.text(title, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 16;

        // Court info
        if (caseDetails?.courtJurisdiction) {
            doc.setFontSize(11);
            doc.setFont(undefined, 'normal');
            const courtInfo = `${caseDetails?.courtJurisdiction}${caseDetails?.jurisdictionState ? `, ${caseDetails?.jurisdictionState}` : ''}`;
            doc.text(courtInfo, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 14;
        }

        // Case number
        if (caseDetails?.caseNumber) {
            doc.setFontSize(11);
            doc.text(`Civil Action File No.: ${caseDetails?.caseNumber}`, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 14;
        }

        // Main title
        yPosition += 12;
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('Marital Balance Sheet', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 16;

        // Marriage date
        if (caseDetails?.marriageDate) {
            doc.setFontSize(11);
            doc.setFont(undefined, 'normal');
            //doc.text(`Date of Marriage: ${formatDate(new Date(caseDetails?.marriageDate))}`, pageWidth / 2, yPosition, { align: 'center' });
            doc.text(`Date of Marriage: ${formatDate(caseDetails?.marriageDate)}`, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 14;
        }

        // Purpose statement
        if (currentCase.caseName) {
            doc.setFontSize(11);
            doc.text(`For litigation purposes only in the matter of ${currentCase.caseName}`, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 20;
        } else {
            yPosition += 20;
        }

        return yPosition;
    };




    return () => {
        const doc = new jsPDF({
            orientation: defaultPageOrientation,
            unit: defaultUnit,
            format: defaultPageFormat
        });

        // Set standard legal document formatting
        doc.setFont("Times", "normal"); // Times New Roman is standard for legal docs





        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 72;
        const contentWidth = pageWidth - (margin * 2);
        const headerSpacing = 20;
        const assetLiabilityTableSpacing = 40;




        // Header
        let yPosition = normalizeMargin(margin).top + headerSpacing;

        yPosition = addMBSTitle(doc, pageWidth, yPosition);

        let rowIndex = 1;
        const amountTextWidth = measureReportAmountTextWidth(doc, data, displayOptions, { includeReportTotals: true, groupByAssignment: true });

        [rowIndex, yPosition] = addAssetLiabilityTables(doc, margin, rowIndex, yPosition, data, "assets", displayOptions, { groupByAssignment: true, amountTextWidth });

        yPosition += assetLiabilityTableSpacing;


        yPosition = addPageIfNeeded(doc, margin, yPosition);

        [rowIndex, yPosition] = addAssetLiabilityTables(doc, margin, rowIndex, yPosition, data, "liabilities", displayOptions, { groupByAssignment: true, amountTextWidth });
        yPosition += assetLiabilityTableSpacing;

        let netWorthTotalData;
        let finalAdjustmentData;

        [netWorthTotalData, rowIndex] = formatAssetLiabilityNetWorthTotalRow(rowIndex, data, displayOptions)
        yPosition = addMBSAccountTable(doc, margin, yPosition, "Total Net Worth", netWorthTotalData, displayOptions, { amountTextWidth });

        // formatFinalAdjustmentTable
        if (displayOptions.showSettlementColumns) {
            yPosition += assetLiabilityTableSpacing;
            yPosition = addPageIfNeeded(doc, margin, yPosition);
            [finalAdjustmentData, rowIndex] = formatFinalAdjustmentTable(rowIndex, data, displayOptions)
            yPosition = addMBSAccountTable(doc, margin, yPosition, "Final Settlement Amount", finalAdjustmentData, displayOptions, { amountTextWidth });
        }

        yPosition += assetLiabilityTableSpacing;
        yPosition = addAbbreviationLegend(doc, margin, yPosition, data);

        addFootersToAllPages(doc, currentOrg, displayOptions, margin)

        const filename = `Marital_Balance_Sheet_${currentCase.caseName ? currentCase.caseName.replace(/[^a-zA-Z0-9]/g, '_') : 'Document'}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(filename);

    }
}

export default usePrintMBSPDF;